import React, { useRef, useState } from "react";
import { ColorPicker } from "./ColorPicker";
import { FontSelector } from "./FontSelector";
import { SizeSelector } from "./SizeSelector";

interface CardEditorProps {
  isDark?: boolean;
}

const CardEditor: React.FC<CardEditorProps> = ({ isDark = true }) => {
  const [backgroundColor, setBackgroundColor] = useState<string>("#9CC2FF");
  const [fontSize, setFontSize] = useState<number>(16);
  const [borderRadius, setBorderRadius] = useState<number>(35);
  const [padding, setPadding] = useState<number>(27);
  const [aspectRatio, setAspectRatio] = useState("3:5");
  const [selectedFont, setSelectedFont] = useState("LXGW WenKai");
  const [text, setText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);

  // 计算卡片容器的样式
  const getCardContainerStyle = () => {
    const ratioMap: { [key: string]: string } = {
      auto: "auto",
      "1:1": "1/1",
      "4:3": "4/3",
      "3:4": "3/4",
      "2:3": "2/3",
      "3:5": "3/5",
      "9:16": "9/16",
      "1:2": "1/2",
    };

    return {
      backgroundColor,
      borderRadius: `${borderRadius}px`,
      padding: `${padding}px`,
      aspectRatio:
        aspectRatio === "auto" ? undefined : ratioMap[aspectRatio] || "1/1",
      width: aspectRatio === "auto" ? "min(400px, 90vw)" : "400px",
      height: aspectRatio === "auto" ? "auto" : undefined,
      maxWidth: "90vw",
      maxHeight: "80vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };
  };

  // 添加渐变色配置
  const getGradientBackground = (baseColor: string | undefined) => {
    if (!baseColor) return "transparent";
    return `linear-gradient(135deg, 
      ${baseColor}80 0%,
      ${baseColor}40 50%,
      ${baseColor}20 100%
    )`;
  };

  // 保存图片
  const saveImage = async () => {
    if (!cardRef.current) return;

    try {
      setIsSaving(true);

      // 创建 canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("无法创建 canvas context");
      }

      // 设置画布大小，添加边距
      const scale = 2; // 2倍清晰度
      const margin = 40 * scale; // 边距
      const width = cardRef.current.offsetWidth * scale;
      const height = cardRef.current.offsetHeight * scale;
      canvas.width = width + margin * 2;
      canvas.height = height + margin * 2;

      // 清空画布（透明背景）
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 绘制背景
      ctx.scale(scale, scale);

      // 创建渐变背景
      const gradient = ctx.createLinearGradient(
        margin / scale,
        margin / scale,
        width / scale,
        height / scale
      );
      gradient.addColorStop(0, `${backgroundColor}80`);
      gradient.addColorStop(0.5, `${backgroundColor}40`);
      gradient.addColorStop(1, `${backgroundColor}20`);

      // 绘制圆角矩形背景
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.roundRect(
        margin / scale,
        margin / scale,
        width / scale,
        height / scale,
        Number(borderRadius)
      );
      ctx.fill();

      // 绘制白色内容区域
      const contentPadding = padding;
      const contentX = margin / scale + contentPadding;
      const contentY = margin / scale + contentPadding;
      const contentWidth = width / scale - contentPadding * 2;
      const contentHeight = height / scale - contentPadding * 2;

      ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
      ctx.beginPath();
      ctx.roundRect(
        contentX,
        contentY,
        contentWidth,
        contentHeight,
        Math.max(Number(borderRadius) - 4, 0)
      );
      ctx.fill();

      // 绘制文本
      ctx.fillStyle = "#1A202C";
      ctx.font = `${fontSize}px ${selectedFont}`;
      ctx.textBaseline = "top";

      const textLines = text.split("\n");
      let y = contentY + contentPadding;
      const lineHeight = fontSize * 1.4;

      textLines.forEach(line => {
        // 处理文本换行
        const words = line.split('');
        let currentLine = '';
        let x = contentX + contentPadding;

        words.forEach(word => {
          const testLine = currentLine + word;
          const metrics = ctx.measureText(testLine);
          if (x + metrics.width > contentX + contentWidth - contentPadding) {
            ctx.fillText(currentLine, x, y);
            currentLine = word;
            y += lineHeight;
            x = contentX + contentPadding;
          } else {
            currentLine += word;
          }
        });
        
        if (currentLine) {
          ctx.fillText(currentLine, x, y);
          y += lineHeight;
        }
      });

      // 导出图片
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `rednote-card-${Date.now()}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("保存图片失败:", error);
      alert("保存图片失败，请重试");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`h-screen flex overflow-hidden ${
        isDark ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      {/* 主内容区域 - 卡片预览 */}
      <div className="flex-1 flex flex-col items-center">
        {/* 颜色选择器 */}
        <div
          className={`mt-6 px-6 py-3 rounded-2xl ${
            isDark
              ? "bg-gray-800/80 backdrop-blur-sm"
              : "bg-gray-50/80 backdrop-blur-sm"
          }`}
        >
          <ColorPicker value={backgroundColor} onChange={setBackgroundColor} />
        </div>

        {/* 卡片预览区域 */}
        <div className="mt-[20px] flex-1 flex items-start justify-center w-full">
          <div
            ref={cardRef}
            style={{
              ...getCardContainerStyle(),
              background: getGradientBackground(backgroundColor),
              fontFamily: selectedFont,
              boxShadow: `0 20px 40px ${backgroundColor}30`,
              transform: "translateZ(0)",
              backfaceVisibility: "hidden",
            }}
            className="relative hover:scale-[1.01] transition-transform duration-300"
          >
            <div
              className="w-full h-full"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.92)",
                borderRadius: `${Math.max(Number(borderRadius) - 4, 0)}px`,
                padding: `${padding}px`,
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="在这里输入文字..."
                className="w-full h-full bg-transparent resize-none focus:outline-none flex-1"
                style={{
                  fontSize: `${fontSize}px`,
                  color: "#1A202C",
                  minHeight: aspectRatio === "auto" ? "200px" : "100%",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  transform: "translateZ(0)",
                  backfaceVisibility: "hidden",
                  overflowWrap: "break-word",
                  wordWrap: "break-word",
                  whiteSpace: "pre-wrap",
                  lineHeight: "1.4",
                  padding: "0",
                  margin: "0",
                  fontFamily: selectedFont,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 右侧控制面板 */}
      <div className="w-[320px] border-l border-gray-700/50 bg-[#1B1B1F] text-gray-100 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* 尺寸比例选择 */}
          <div>
            <h3 className="text-gray-300 mb-3">尺寸</h3>
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: "自动", value: "auto" },
                { label: "1:1", value: "1:1" },
                { label: "4:3", value: "4:3" },
                { label: "2:3", value: "2:3" },
                { label: "3:5", value: "3:5" },
                { label: "9:16", value: "9:16" },
                { label: "1:2", value: "1:2" },
                { label: "3:4", value: "3:4" },
              ].map((size) => (
                <button
                  key={size.value}
                  className={`px-3 py-1 rounded ${
                    size.value === aspectRatio
                      ? "bg-gray-600 text-white"
                      : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                  }`}
                  onClick={() => setAspectRatio(size.value)}
                >
                  {size.label}
                </button>
              ))}
            </div>
          </div>

          {/* 字号设置 */}
          <div>
            <h3 className="text-gray-300 mb-3">字号</h3>
            <SizeSelector
              value={fontSize}
              onChange={setFontSize}
              min={12}
              max={32}
              step={1}
            />
            <div className="text-gray-400 text-sm mt-1">{fontSize}px</div>
          </div>

          {/* 圆角设置 */}
          <div>
            <h3 className="text-gray-300 mb-3">圆角</h3>
            <SizeSelector
              value={borderRadius}
              onChange={setBorderRadius}
              min={0}
              max={60}
              step={5}
            />
            <div className="text-gray-400 text-sm mt-1">{borderRadius}px</div>
          </div>

          {/* 边距设置 */}
          <div>
            <h3 className="text-gray-300 mb-3">边距</h3>
            <SizeSelector
              value={padding}
              onChange={setPadding}
              min={16}
              max={60}
              step={1}
            />
            <div className="text-gray-400 text-sm mt-1">{padding}px</div>
          </div>

          {/* 字体选 */}
          <div>
            <h3 className="text-gray-300 mb-3">字体</h3>
            <FontSelector value={selectedFont} onChange={setSelectedFont} />
          </div>

          {/* 保存按钮 */}
          <div className="pt-4">
            <button
              onClick={saveImage}
              disabled={isSaving}
              className={`w-full ${
                isSaving
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-red-500 hover:bg-red-600"
              } text-white py-2 rounded-lg transition-colors`}
            >
              {isSaving ? "保存中..." : "保存图片"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardEditor;

